import * as THREE from 'three';
import { RWR, SIM } from '../config/tuning';
import {
  DEMO_THREAT_LAYOUT,
  THREATS,
  ThreatClass,
  type ThreatParams,
  type ThreatPlacement,
} from '../config/threats';
import type { Checksum, Checksummable } from '../core/Checksum';
import { bus } from '../core/EventBus';
import {
  RwrState,
  type MissileWarning,
  type RwrContact,
  type RwrStateId,
} from '../hud/HudFrame';
import type { Mission } from '../mission/schema';
import type { Terrain } from '../world/Terrain';
import type { Atmosphere } from '../flight/Atmosphere';
import { AirDefenceSite, SiteState, type LaunchIntent } from './AirDefenceSite';
import type { RadarTarget } from './Detection';
import { EwField } from './EW';
import { Missile, MissileOutcome, damageAspectFactor, detonationDamage } from './Missile';
import { createSeekerContext, LockState, type SeekerContext } from './Seeker';
import type { Countermeasures } from './Countermeasures';
import { MISSILE } from '../config/weapons';
import { Pool } from '../core/Pool';

/**
 * Every threat in the theatre, and the clock that drives them (SPEC §3.5).
 *
 * **Detection runs at 10 Hz, staggered.** Not per frame, and not all sites on the same tick: each
 * site is assigned a phase so the work spreads evenly across the 12 ticks of a detection period.
 * With twelve sites that is exactly one line-of-sight ray march per tick, which is what keeps the
 * whole threat picture inside the half-millisecond budget of the M5 acceptance criteria.
 *
 * The staggering is deterministic — phase is the site's index, not a random draw — so the same
 * flight produces the same detections tick for tick.
 */

/** Simulation ticks between evaluations of a given site. */
const TICKS_PER_DETECTION = Math.round(1 / (SIM.FIXED_DT * SIM.DETECTION_HZ));

/** Reused payload: emitted from inside the tick, so it must not allocate (SPEC §4.3). */
const launchIntent: LaunchIntent = {
  siteId: -1,
  designation: '',
  originM: new THREE.Vector3(),
  salvoIndex: 0,
};

const relativeScratch = new THREE.Vector3();

/** Reused payload for the detonation event; emitted inside the tick (SPEC §4.3). */
const impact = {
  designation: '',
  missDistanceM: 0,
  damage: 0,
  positionM: new THREE.Vector3(),
  destroyed: false,
};

export class ThreatField implements Checksummable {
  readonly sites: AirDefenceSite[] = [];
  readonly ew = new EwField();

  /** Contacts for the RWR ring. Rebuilt in place each frame; never re-allocated. */
  private readonly contactPool: RwrContact[] = [];
  private readonly contacts: RwrContact[] = [];

  /** Missile warning carets, likewise pre-allocated. */
  private readonly warningPool: MissileWarning[] = [];
  private readonly warnings: MissileWarning[] = [];

  /** Missiles in the air. Pooled: a salvo must not allocate mid-engagement. */
  readonly missiles: Missile[] = [];
  private readonly missilePool = new Pool<Missile>(
    'missile',
    () => new Missile(),
    (missile) => {
      missile.active = false;
    },
    MISSILE.poolCapacity,
  );

  private readonly terrain: Terrain;
  private readonly atmosphere: Atmosphere;
  private seekerContext: SeekerContext | null = null;
  private tick = 0;
  private nextMissileId = 0;

  constructor(terrain: Terrain, atmosphere: Atmosphere) {
    this.terrain = terrain;
    this.atmosphere = atmosphere;
  }

  /** Live contacts, aircraft-relative, for `HudFrame.rwrContacts`. */
  get rwrContacts(): readonly RwrContact[] {
    return this.contacts;
  }

  /** Place a threat. Missions call this at M8; `loadDemoLayout` does it now. */
  add(placement: ThreatPlacement): void {
    if (placement.type === ThreatClass.EwJammer) {
      this.ew.add(placement, this.terrain);
      return;
    }
    const params = THREATS[placement.type];
    const site = new AirDefenceSite(this.sites.length, placement, params, this.terrain);
    this.sites.push(site);
    this.contactPool.push({ relativeBearingRad: 0, state: RwrState.Search, label: '' });
  }

  /** The placeholder threat laydown of `DEMO_THREAT_LAYOUT`, used by Free Flight and the harnesses. */
  loadDemoLayout(): void {
    for (let i = 0; i < DEMO_THREAT_LAYOUT.length; i += 1) this.add(DEMO_THREAT_LAYOUT[i]);
  }

  /**
   * Place a mission's threats, applying per-site overrides (SPEC §6.1).
   *
   * Overrides are applied to a **copy** of the catalogue entry. `THREATS[type]` is a shared module
   * object: writing a mission's `crewQuality` into it would retune that threat class for every
   * later mission in the session, and the bug would present as difficulty that drifts between
   * missions rather than as anything traceable to this line.
   */
  loadMission(mission: Mission): void {
    for (let i = 0; i < mission.threats.length; i += 1) {
      const entry = mission.threats[i];
      const placement: ThreatPlacement = {
        designation: entry.id,
        type: entry.type,
        xM: entry.position[0],
        zM: entry.position[2],
      };
      if (entry.type === ThreatClass.EwJammer) {
        this.ew.add(placement, this.terrain);
        continue;
      }
      const params: ThreatParams = { ...THREATS[entry.type], ...(entry.overrides ?? {}) };
      const site = new AirDefenceSite(this.sites.length, placement, params, this.terrain);
      site.enabled = entry.active;
      this.sites.push(site);
      this.contactPool.push({ relativeBearingRad: 0, state: RwrState.Search, label: '' });
    }
  }

  /**
   * Clear every placement, so a new mission starts from an empty theatre.
   *
   * Distinct from `reset`, which restores the *state* of the sites already placed. Loading a
   * second mission without this leaves the first mission's batteries in the world, which presents
   * as a mission that is inexplicably harder the second time it is flown.
   */
  resetLayout(): void {
    this.reset();
    this.sites.length = 0;
    this.contactPool.length = 0;
    this.warningPool.length = 0;
    this.ew.clear();
  }

  /** Apply the trigger system's live set of active threat ids (SPEC §3.10.1). */
  applyActive(active: ReadonlySet<string>): void {
    for (let i = 0; i < this.sites.length; i += 1) {
      const site = this.sites[i];
      site.enabled = active.has(site.designation);
    }
  }

  /** Threat designation → state machine label, for `threatState` triggers. */
  stateSnapshot(into: Map<string, string>): Map<string, string> {
    into.clear();
    for (let i = 0; i < this.sites.length; i += 1) {
      into.set(this.sites[i].designation, this.sites[i].state);
    }
    return into;
  }

  reset(): void {
    for (let i = 0; i < this.sites.length; i += 1) this.sites[i].reset();
    for (let i = this.missiles.length - 1; i >= 0; i -= 1) {
      this.missilePool.release(this.missiles[i]);
    }
    this.missiles.length = 0;
    this.ew.reset();
    this.contacts.length = 0;
    this.tick = 0;
  }

  /** Which site fired a given missile, or null if it has been removed. */
  private siteFor(siteId: number): AirDefenceSite | null {
    return siteId >= 0 && siteId < this.sites.length ? this.sites[siteId] : null;
  }

  /**
   * Fly every missile in the air (step 5 of the tick order, SPEC §4.3).
   *
   * Command guidance is fed the firing site's **live** return, not its state-machine state. A
   * site in TRACKING is still coasting on `t_memory` and has nothing current to send up the
   * uplink, so the notch cuts guidance the instant the return is lost rather than four seconds
   * later. That distinction is what makes the break turn of SPEC §3.6.5 work at all.
   */
  private updateMissiles(
    dtS: number,
    target: RadarTarget,
    countermeasures: Countermeasures,
  ): void {
    if (this.seekerContext === null) {
      this.seekerContext = createSeekerContext(target, this.terrain);
    }
    const context = this.seekerContext;
    context.target = target;
    context.decoys = countermeasures.active;

    for (let i = this.missiles.length - 1; i >= 0; i -= 1) {
      const missile = this.missiles[i];
      const site = this.siteFor(missile.siteId);
      const siteHasTrack = site !== null && site.tests.detected;

      const outcome = missile.tick(dtS, context, this.atmosphere, siteHasTrack);
      if (outcome === MissileOutcome.Flying) continue;

      if (outcome === MissileOutcome.Detonated) {
        impact.designation = missile.designation;
        impact.missDistanceM = missile.missDistanceM;
        impact.damage = detonationDamage(
          missile.missDistanceM,
          missile.params.fuseRadiusM,
          missile.params.warheadYield,
          damageAspectFactor(missile.velocityMs, target.forward),
        );
        impact.positionM.copy(missile.detonationM);
        impact.destroyed = false;
        bus.emit('missile:detonated', impact);
      } else {
        impact.designation = missile.designation;
        impact.missDistanceM = missile.missDistanceM;
        impact.damage = 0;
        impact.positionM.copy(missile.detonationM);
        impact.destroyed = false;
        bus.emit('missile:expired', impact);
      }

      // Swap-remove and return to the pool.
      this.missiles[i] = this.missiles[this.missiles.length - 1];
      this.missiles.pop();
      this.missilePool.release(missile);
    }
  }

  /** Turn a site's launch intent into a missile (SPEC §3.6.1). */
  private spawnMissile(intent: LaunchIntent, target: RadarTarget): void {
    const site = this.siteFor(intent.siteId);
    if (site === null) return;

    const missile = this.missilePool.acquire();
    this.nextMissileId += 1;
    // Seeded from the missile's own id, so a decoy that works in one replay works in the next.
    missile.launch(
      this.nextMissileId,
      intent.siteId,
      intent.designation,
      site.params.missile,
      intent.originM,
      target.positionM,
      (this.nextMissileId * 0x9e3779b1) >>> 0,
    );
    this.missiles.push(missile);
    while (this.warningPool.length < this.missiles.length) {
      this.warningPool.push({ relativeBearingRad: 0, closureMs: 0 });
    }
  }

  /**
   * Steps 7 and 8 of the tick order (SPEC §4.3): detection and the site state machines.
   *
   * Returns the link quality after jamming, so the caller can hand one number to both the HUD and
   * the post stack.
   */
  update(
    dtS: number,
    target: RadarTarget,
    nowS: number,
    countermeasures: Countermeasures,
  ): number {
    const detectionDtS = TICKS_PER_DETECTION * dtS;

    for (let i = 0; i < this.sites.length; i += 1) {
      // Staggered: site i is evaluated on the tick where the period boundary lines up with its
      // index, so no single tick pays for more than one or two sites.
      if ((this.tick + i) % TICKS_PER_DETECTION !== 0) continue;

      const site = this.sites[i];
      const intent = site.update(detectionDtS, this.terrain, target, nowS, launchIntent);
      if (intent !== null) {
        bus.emit('threat:launch', intent);
        this.spawnMissile(intent, target);
      }
    }

    // 5. Missile physics and guidance, every tick — a 120 Hz fuse cannot run at 10 Hz.
    this.updateMissiles(dtS, target, countermeasures);

    this.tick += 1;
    return this.ew.tick(dtS, target.positionM, this.terrain);
  }

  /**
   * Rebuild the RWR contact list for this frame (SPEC §3.4.1).
   *
   * The RWR is a receiver, not a report of who has locked you: a site shows up when the aircraft
   * can hear it radiating, whether or not that site can currently resolve a track. That
   * distinction is the entire feedback loop of the notch — the contact stays on the ring and
   * *changes glyph* from track to search as the return fades, which is how the player learns that
   * the heading they just rolled onto is doing something.
   */
  buildRwrContacts(headingRad: number, aircraftPositionM: THREE.Vector3): readonly RwrContact[] {
    this.contacts.length = 0;

    for (let i = 0; i < this.sites.length; i += 1) {
      const site = this.sites[i];
      if (!site.isEmitting) continue;

      // Hearing an emitter needs line of sight to it and enough proximity for the sidelobes to
      // reach — a little beyond its own search range, since a receiver is more sensitive than the
      // radar's own return path.
      const hearingRangeM = site.params.searchRangeM * RWR.hearingRangeFactor;
      if (site.tests.rangeM > hearingRangeM || !site.tests.hasLineOfSight) continue;

      const contact = this.contactPool[i];
      relativeScratch.copy(site.positionM).sub(aircraftPositionM);
      const bearingRad = Math.atan2(relativeScratch.x, -relativeScratch.z);
      contact.relativeBearingRad = wrapPi(bearingRad - headingRad);
      contact.state = rwrStateFor(site);
      contact.label = site.params.rwrLabel;
      this.contacts.push(contact);
    }

    return this.contacts;
  }

  /**
   * Missile warnings for the HUD (SPEC §3.4.1): a screen-edge caret per inbound, with the closure
   * rate that drives its tick frequency.
   *
   * Only missiles that are actually guiding on the aircraft appear. A round that has gone
   * ballistic is still dangerous but is no longer *tracking*, and leaving its caret up would tell
   * the player their break failed when it succeeded — which would teach exactly the wrong lesson.
   */
  buildMissileWarnings(
    headingRad: number,
    aircraftPositionM: THREE.Vector3,
  ): readonly MissileWarning[] {
    this.warnings.length = 0;

    for (let i = 0; i < this.missiles.length; i += 1) {
      const missile = this.missiles[i];
      if (missile.seeker.state === LockState.Ballistic) continue;
      if (missile.closureMs <= 0) continue;

      const warning = this.warningPool[i];
      relativeScratch.copy(missile.positionM).sub(aircraftPositionM);
      const bearingRad = Math.atan2(relativeScratch.x, -relativeScratch.z);
      warning.relativeBearingRad = wrapPi(bearingRad - headingRad);
      warning.closureMs = missile.closureMs;
      this.warnings.push(warning);
    }

    return this.warnings;
  }

  checksum(into: Checksum): void {
    for (let i = 0; i < this.sites.length; i += 1) this.sites[i].checksum(into);
    into.addInt(this.missiles.length);
    for (let i = 0; i < this.missiles.length; i += 1) this.missiles[i].checksum(into);
    this.ew.checksum(into);
  }
}

/**
 * Which glyph a site draws on the ring.
 *
 * A tracking site whose Doppler return has faded below the floor reads as SEARCH again even
 * though the state machine is still coasting on track memory. That is not a lie to the player —
 * it is what the receiver would actually hear, because a radar that has lost its return stops
 * transmitting a lock tone — and it is the immediate feedback that makes notching learnable
 * inside one turn rather than one mission.
 */
function rwrStateFor(site: AirDefenceSite): RwrStateId {
  if (site.state === SiteState.Launching || site.state === SiteState.Guiding) {
    return RwrState.Launch;
  }
  if (site.state === SiteState.Tracking) {
    return site.tests.dopplerVisibility >= RWR.trackVisibilityFloor && site.tests.hasLineOfSight
      ? RwrState.Track
      : RwrState.Search;
  }
  return RwrState.Search;
}

/** Wrap an angle to (−π, π]. */
export function wrapPi(angleRad: number): number {
  const twoPi = Math.PI * 2;
  let wrapped = angleRad % twoPi;
  if (wrapped > Math.PI) wrapped -= twoPi;
  if (wrapped <= -Math.PI) wrapped += twoPi;
  return wrapped;
}
